import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxesStacked,
  faSpinner,
  faTriangleExclamation,
  faWarehouse,
  faCalendarDay,
  faMagnifyingGlass,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import "../styles/itemCurrentStockModal.css";

// Only these roles are allowed to open this modal via the global shortcut.
const ALLOWED_ROLES = ["Authorized Person", "itManager"];

const formatDate = (date) => (date ? moment(date).format("DD-MMM-YYYY") : "—");

// Keeps react-select visually in sync with the rest of the modal, and
// makes sure the option list is roomy (not the cramped default) and is
// never clipped by the modal's own overflow/scroll container.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 8,
    borderColor: state.isFocused ? "#5b8def" : "#dfe3e8",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(91,141,239,0.15)" : "none",
    "&:hover": { borderColor: "#5b8def" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
  placeholder: (base) => ({ ...base, color: "#9aa2ab" }),
  // Rendered in a portal so the modal's overflow/scroll never clips it.
  menuPortal: (base) => ({ ...base, zIndex: 1000050 }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 12px 28px rgba(20,20,30,0.18)",
    border: "1px solid #eceff2",
  }),
  menuList: (base) => ({ ...base, maxHeight: 260, padding: 6 }),
  option: (base, state) => ({
    ...base,
    borderRadius: 6,
    padding: "9px 12px",
    fontSize: "0.9rem",
    cursor: "pointer",
    backgroundColor: state.isSelected
      ? "#5b8def"
      : state.isFocused
      ? "#eef3fe"
      : "transparent",
    color: state.isSelected ? "#fff" : "#222",
  }),
};

/**
 * ItemCurrentStockModal
 *
 * A globally-mounted, role-gated modal that shows an item's current
 * (closing) stock, overall and department-wise.
 *
 * - Opens with Ctrl+Q (toggles open/closed) from anywhere in the app.
 * - Closes with Esc, or Ctrl+Q again. There is no click-outside-to-close
 *   and no close button, by design.
 * - Mount this ONCE at the top of the app's layout (e.g. alongside your
 *   <Routes /> in dashboard.jsx) so the shortcut works on every screen.
 */
const ItemCurrentStockModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const [itemOptions, setItemOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState("");

  const [selectedItem, setSelectedItem] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState("");

  const role = localStorage.getItem("role");
  const isAuthorized = ALLOWED_ROLES.includes(role);

  const resetSelection = useCallback(() => {
    setSelectedItem(null);
    setStockData(null);
    setStockError("");
  }, []);

  // ---- Global shortcut: Ctrl+Q toggles, Esc closes ----
  useEffect(() => {
    if (!isAuthorized) return undefined;

    const handleKeyDown = (e) => {
      const key = (e.key || "").toLowerCase();
      const isCtrlQ = (e.ctrlKey || e.metaKey) && !e.altKey && key === "q";

      if (isCtrlQ) {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen((prev) => {
          const next = !prev;
          if (!next) resetSelection();
          return next;
        });
        return;
      }

      if (key === "escape") {
        setIsOpen((prev) => {
          if (!prev) return prev;
          resetSelection();
          return false;
        });
      }
    };

    // capture: true so this fires even if focus is inside an input/select
    // elsewhere on the page (or inside the modal itself).
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [isAuthorized, resetSelection]);

  // Lock background scroll while open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Load item options the first time the modal is opened.
  useEffect(() => {
    if (!isOpen || itemOptions.length > 0) return;
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchItems = async () => {
    const code = localStorage.getItem("code");
    const token = localStorage.getItem("token");

    setOptionsLoading(true);
    setOptionsError("");
    try {
      const response = await apiClient.get(SERVER_BASE + "/get-items-all", {
        params: { centerCode: code },
        headers: { Authorization: `Bearer ${token}` },
      });

      const items = Array.isArray(response.data)
        ? response.data
        : response.data?.items || [];

      setItemOptions(
        items.map((item) => ({
          value: item.id,
          label: item.printName,
        }))
      );
    } catch (error) {
      console.error("Error fetching items:", error);
      setOptionsError("Failed to load items. Please try again.");
    } finally {
      setOptionsLoading(false);
    }
  };

  const fetchClosingStock = async (itemId) => {
    const token = localStorage.getItem("token");

    setStockLoading(true);
    setStockError("");
    setStockData(null);
    try {
      const response = await apiClient.get(
        SERVER_BASE + "/getItemClosingStock",
        {
          params: { itemId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStockData(response.data);
    } catch (error) {
      console.error("Error fetching item closing stock:", error);
      setStockError("Failed to load current stock for this item.");
    } finally {
      setStockLoading(false);
    }
  };

  const handleItemChange = (option) => {
    setSelectedItem(option);
    setStockData(null);
    setStockError("");
    if (option) {
      fetchClosingStock(option.value);
    }
  };

  if (!isAuthorized || !isOpen) return null;

  return createPortal(
    <div className="icsm-overlay">
      <div
        className="icsm-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Item Current Stock"
      >
        <div className="icsm-header">
          <div className="icsm-header-title">
            <span className="icsm-header-icon">
              <FontAwesomeIcon icon={faBoxesStacked} />
            </span>
            <div>
              <h3>Item Current Stock</h3>
              <p>Look up live closing stock, overall and by department</p>
            </div>
          </div>
          <div className="icsm-hint">
            <kbd>Esc</kbd>
            <span>or</span>
            <kbd>Ctrl</kbd>
            <span>+</span>
            <kbd>Q</kbd>
            <span>to close</span>
          </div>
        </div>

        <div className="icsm-body">
          <label className="icsm-label">
            <FontAwesomeIcon icon={faMagnifyingGlass} /> Select Item
          </label>
          <Select
            classNamePrefix="icsm-select"
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            options={itemOptions}
            value={selectedItem}
            onChange={handleItemChange}
            isLoading={optionsLoading}
            isClearable
            autoFocus
            placeholder="Search item by name..."
            noOptionsMessage={() =>
              optionsError ? optionsError : "No items found"
            }
          />
          {optionsError && (
            <div className="icsm-error">
              <FontAwesomeIcon icon={faTriangleExclamation} /> {optionsError}
            </div>
          )}

          {stockLoading && (
            <div className="icsm-loading">
              <FontAwesomeIcon icon={faSpinner} spin /> Loading current
              stock...
            </div>
          )}
          {stockError && (
            <div className="icsm-error">
              <FontAwesomeIcon icon={faTriangleExclamation} /> {stockError}
            </div>
          )}

          {stockData && !stockLoading && !stockError && (
            <div className="icsm-results">
              <div className="icsm-summary">
                <div className="icsm-summary-item icsm-summary-primary">
                  <span className="icsm-summary-icon">
                    <FontAwesomeIcon icon={faWarehouse} />
                  </span>
                  <div>
                    <span className="icsm-summary-label">
                      Overall Closing Qty
                    </span>
                    <span className="icsm-summary-value">
                      {stockData.closingQty}
                    </span>
                  </div>
                </div>
                <div className="icsm-summary-item">
                  <span className="icsm-summary-icon">
                    <FontAwesomeIcon icon={faCalendarDay} />
                  </span>
                  <div>
                    <span className="icsm-summary-label">As of</span>
                    <span className="icsm-summary-value icsm-summary-date">
                      {formatDate(stockData.date)}
                    </span>
                  </div>
                </div>
              </div>

              {stockData.departmentWiseClosing?.length > 0 && (
                <div className="icsm-table-wrap">
                  <table className="icsm-table">
                    <thead>
                      <tr>
                        <th>Department</th>
                        <th>Closing Qty</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockData.departmentWiseClosing.map((row, idx) => (
                        <tr key={`${row.department}-${idx}`}>
                          <td>{row.department}</td>
                          <td className="icsm-qty-cell">{row.closingQty}</td>
                          <td>{formatDate(row.date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {!selectedItem && !stockLoading && (
            <div className="icsm-placeholder">
              <FontAwesomeIcon icon={faCircleInfo} />
              <span>Select an item above to view its current stock.</span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ItemCurrentStockModal;
